import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { EmailVerificationModal } from '@/components/modals/EmailVerificationModal';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import authBg from '@/components/images/background-auth/01-background-auth.jpg';
import onboardingLogo from '@/components/images/logo/logo-orcamigo-onboarding.png';
import { normalizeReferralCode, storeReferralCode } from '@/lib/referral';
import { sanitizeInternalPath, POST_AUTH_REDIRECT_KEY, APP_URL } from '@/lib/urls';

// Detect if running in a native Capacitor environment (Android/iOS)
const isCapacitor = Capacitor.isNativePlatform();

// Google icon component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, signUp, user, loading } = useAuth();
  const referralCode = normalizeReferralCode(searchParams.get('ref'));

  // Where to send the user after a successful auth (e.g. deep-linked from an
  // invite). Only same-origin internal paths are honored. Default straight to
  // /dashboard so login/Google don't bounce through "/" (Onboarding) first.
  const nextPath = sanitizeInternalPath(searchParams.get('next'), '/dashboard');

  // Get default tab from URL params
  const defaultTab = referralCode || searchParams.get('tab') === 'signup' ? 'signup' : 'login';
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot-password flow
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Signup form
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupConfirmEmail, setSignupConfirmEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');

  // Persist the referral before any password or OAuth redirect starts.
  useEffect(() => {
    if (referralCode) storeReferralCode(referralCode);
  }, [referralCode]);

  // Persist the post-auth redirect so it survives an OAuth round-trip through
  // /auth/callback (where the URL no longer carries our `next` param).
  useEffect(() => {
    try {
      if (nextPath && nextPath !== '/dashboard') {
        sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, nextPath);
      }
    } catch {
      // sessionStorage may be unavailable in hardened contexts.
    }
  }, [nextPath]);

  // Redirect once the session resolves — in an effect, not during render (a
  // render-phase navigate is a side effect and warns/double-fires).
  useEffect(() => {
    if (!loading && user) navigate(nextPath, { replace: true });
  }, [loading, user, nextPath, navigate]);

  // Don't flash the login form while the session is still restoring (offline
  // cache can take a moment), nor during the redirect above.
  if (loading || user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    
    try {
      if (isCapacitor) {
        // Native Google Auth for Capacitor (mobile)
        console.log('[GoogleAuth] Starting native sign-in...');
        
        let googleUser;
        try {
          googleUser = await GoogleAuth.signIn();
          console.log('[GoogleAuth] Sign-in response:', JSON.stringify(googleUser, null, 2));
        } catch (signInError: any) {
          console.error('[GoogleAuth] signIn() failed:', signInError);
          
          // Check if user cancelled
          if (signInError?.message?.includes('cancel') || signInError?.code === 12501) {
            toast.warning('Login cancelado pelo usuário');
            return;
          }
          
          // Show detailed error for debugging
          const errorMsg = signInError?.message || signInError?.error || JSON.stringify(signInError);
          toast.error(`Erro no Google: ${errorMsg}`);
          return;
        }
        
        // Get the ID token from the native sign-in
        const idToken = googleUser?.authentication?.idToken;
        const accessToken = googleUser?.authentication?.accessToken;
        
        console.log('[GoogleAuth] idToken exists:', !!idToken);
        console.log('[GoogleAuth] accessToken exists:', !!accessToken);
        
        if (!idToken) {
          console.error('[GoogleAuth] No idToken in response:', googleUser);
          toast.error('Token não recebido do Google. Verifique a configuração do Client ID.');
          return;
        }
        
        // Send the token to Supabase for validation
        console.log('[Supabase] Calling signInWithIdToken...');
        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: idToken,
        });

        if (error) {
          console.error('[Supabase] signInWithIdToken error:', error);
          toast.error(`Erro Supabase: ${error.message}`);
          return;
        }

        console.log('[Supabase] Login successful:', data?.user?.email);
        toast.success('Login realizado com sucesso!');
        navigate(nextPath, { replace: true });
      } else {
        // Web OAuth flow (fallback for browser)
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
          },
        });

        if (error) {
          throw error;
        }
        // User will be redirected to Google's login page
      }
    } catch (error: any) {
      console.error('[GoogleAuth] Unexpected error:', error);
      const errorMessage = error?.message || error?.error || 'Erro desconhecido';
      toast.error(`Erro: ${errorMessage}`);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      toast.warning('Preencha todos os campos');
      return;
    }

    setIsLoading(true);
    const { error } = await signIn(loginEmail, loginPassword);
    setIsLoading(false);

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        // Offer a direct path to recovery instead of a dead-end error.
        toast.warning('E-mail ou senha incorretos', {
          action: { label: 'Esqueci a senha', onClick: openForgotDialog },
        });
      } else if (error.message.includes('Email not confirmed')) {
        // Route the unconfirmed user into the in-app OTP screen: they enter the code
        // from their signup e-mail, or resend from there (which respects the
        // cooldown). We do NOT resend here — that would risk an immediate
        // double-send / rate-limit right as the modal opens.
        setRegisteredEmail(loginEmail);
        setShowVerificationModal(true);
        toast.warning('Confirme seu e-mail para entrar. Digite o código do e-mail de cadastro ou reenvie.');
      } else {
        toast.error('Erro ao fazer login. Tente novamente.');
      }
      return;
    }

    toast.success('Login realizado com sucesso!');
    navigate(nextPath, { replace: true });
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signupName.trim() || !signupEmail || !signupConfirmEmail || !signupPassword || !signupConfirmPassword) {
      toast.warning('Preencha todos os campos');
      return;
    }

    if (signupName.trim().length < 2) {
      toast.warning('O nome deve ter pelo menos 2 caracteres');
      return;
    }

    if (signupEmail !== signupConfirmEmail) {
      toast.warning('Os e-mails não coincidem');
      return;
    }

    if (signupPassword !== signupConfirmPassword) {
      toast.warning('As senhas não coincidem');
      return;
    }

    if (signupPassword.length < 6) {
      toast.warning('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setIsLoading(true);
    // The referral code is already persisted to sessionStorage (see the effect
    // above) and is attributed after authentication by AuthContext, so it does
    // not need to be threaded through signUp.
    const { error } = await signUp(signupEmail, signupPassword, signupName.trim());
    setIsLoading(false);

    if (error) {
      if (error.message.includes('User already registered')) {
        toast.warning('Este e-mail já está cadastrado');
      } else {
        toast.error('Erro ao criar conta. Tente novamente.');
      }
      return;
    }

    setRegisteredEmail(signupEmail);
    setShowVerificationModal(true);
  };

  const handleResendEmail = async (): Promise<boolean> => {
    if (!registeredEmail) return false;

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: registeredEmail,
    });

    if (error) {
      toast.error('Erro ao reenviar e-mail. Tente novamente.');
      return false;
    }

    toast.success('E-mail de confirmação reenviado!');
    return true;
  };

  // Verify the 6-digit code from the confirmation email. Keeps the user in-app
  // (no link round-trip / deep link), so it works identically on web and native
  // and survives reading the code on another device. On success the session is
  // established (onAuthStateChange → SIGNED_IN) and we honor nextPath (e.g. an
  // invite). The email's link still works as a fallback.
  const handleVerifyOtp = async (code: string): Promise<{ error: Error | null }> => {
    const { error } = await supabase.auth.verifyOtp({
      email: registeredEmail,
      token: code,
      type: 'signup',
    });

    if (error) {
      return { error: error as Error | null };
    }

    toast.success('E-mail confirmado! Bem-vindo ao Orçamigo.');
    setShowVerificationModal(false);
    navigate(nextPath, { replace: true });
    return { error: null };
  };

  const openForgotDialog = () => {
    // Pre-fill with whatever the user already typed in the login form.
    setForgotEmail(loginEmail);
    setForgotOpen(true);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = forgotEmail.trim();
    if (!email) {
      toast.warning('Informe seu e-mail');
      return;
    }

    setForgotLoading(true);
    // On native (Capacitor) the origin is localhost, so force the production web
    // URL; on web keep the current origin so dev/staging links stay on the same
    // host instead of bouncing to production.
    const redirectBase = isCapacitor ? APP_URL : window.location.origin;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${redirectBase}/auth/reset-password`,
    });
    setForgotLoading(false);

    if (error) {
      toast.error('Erro ao enviar o e-mail. Tente novamente.');
      return;
    }

    // Always report success (avoid leaking which e-mails have accounts).
    toast.success('Se houver uma conta com esse e-mail, enviamos um link para redefinir a senha.');
    setForgotOpen(false);
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center px-4 py-8">
      {/* 1. Background Image (z-0) */}
      <div
        className="fixed inset-0 bg-cover bg-center z-0"
        style={{ backgroundImage: `url(${authBg})` }}
      />

      {/* 2. Dark Overlay Gradient (z-10) */}
      <div
        className="fixed inset-0 z-10 pointer-events-none"
        style={{
          background:
            'linear-gradient(to bottom, transparent 0%, rgba(15, 15, 15, 0.4) 40%, rgba(15, 15, 15, 0.95) 100%)',
        }}
      />

      {/* 3. Content (z-20) */}
      <div className="relative z-20 mb-8">
        <img
          src={onboardingLogo}
          alt="Orçamigo"
          className="h-10 w-auto"
        />
      </div>

      <Card className="relative z-20 w-full max-w-md">
        <Tabs defaultValue={defaultTab}>
          <CardHeader>
            {/* Alternador estilizado como o botão 3D (padrão #20): a aba ativa vira
                uma pill verde elevada; a base de 4px empurra o texto ativo ~2px pra
                cima (centraliza na face clara). */}
            {/* Alternador-pill padrão (azul) — estilo no componente base ui/tabs. */}
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Entrar</TabsTrigger>
              <TabsTrigger value="signup">Criar conta</TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent>
            <TabsContent value="login" className="space-y-4">
              {/* Google Login Button */}
              <Button3D
                type="button"
                color="light"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading || isLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="ml-2">Continuar com Google</span>
              </Button3D>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative pt-1">
                  <Label htmlFor="login-email" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">E-mail</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                  />
                </div>

                <div className="relative pt-1">
                  <Label htmlFor="login-password" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">Senha</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    onClick={openForgotDialog}
                    className="text-xs text-muted-foreground hover:text-primary hover:underline"
                  >
                    Esqueci minha senha?
                  </button>
                </div>

                <Button3D type="submit" className="w-full" disabled={isLoading || isGoogleLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button3D>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="space-y-4">
              {/* Google Signup Button */}
              <Button3D
                type="button"
                color="light"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading || isLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="ml-2">Continuar com Google</span>
              </Button3D>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <form onSubmit={handleSignup} className="space-y-4">
                <div className="relative pt-1">
                  <Label htmlFor="signup-name" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">Seu nome *</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Digite seu nome"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                  />
                </div>

                <div className="relative pt-1">
                  <Label htmlFor="signup-email" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">E-mail</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                  />
                </div>

                <div className="relative pt-1">
                  <Label htmlFor="signup-confirm-email" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">Confirmar e-mail</Label>
                  <Input
                    id="signup-confirm-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={signupConfirmEmail}
                    onChange={(e) => setSignupConfirmEmail(e.target.value)}
                    // Confirmação de e-mail: bloqueia colar/arrastar SEMPRE (o
                    // propósito do campo é forçar a redigitação para pegar erros de
                    // digitação — colar o mesmo e-mail errado anula a checagem).
                    onPaste={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                  />
                </div>

                <div className="relative pt-1">
                  <Label htmlFor="signup-password" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">Senha</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mínimo 6 caracteres"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="relative pt-1">
                  <Label htmlFor="signup-confirm-password" className="absolute left-3 -top-2 z-10 bg-card px-1 text-xs">Confirmar senha</Label>
                  <div className="relative">
                    <Input
                      id="signup-confirm-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Repita a senha"
                      value={signupConfirmPassword}
                      onChange={(e) => setSignupConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button3D type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Criando conta...
                    </>
                  ) : (
                    'Criar conta'
                  )}
                </Button3D>
              </form>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Footer Links */}
      <div className="relative z-20 mt-6 flex flex-col items-center gap-2 text-xs text-white/80">
        <div className="flex items-center gap-4">
          <Link to="/terms" className="hover:text-white hover:underline">
            Termos de uso
          </Link>
          <span>•</span>
          <Link to="/privacy" className="hover:text-white hover:underline">
            Privacidade
          </Link>
          <span>•</span>
          <Link to="/help" className="hover:text-white hover:underline">
            Ajuda
          </Link>
        </div>
      </div>

      {/* App Version — injected by vite.config.ts from android/app/build.gradle */}
      <div className="fixed bottom-4 right-4 z-20 text-xs text-white/70">
        v{__APP_VERSION_CODE__} ({__APP_VERSION_NAME__})
      </div>

      <EmailVerificationModal
        open={showVerificationModal}
        onOpenChange={setShowVerificationModal}
        email={registeredEmail}
        onVerify={handleVerifyOtp}
        onResend={handleResendEmail}
      />

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Redefinir senha</DialogTitle>
            <DialogDescription>
              Informe o e-mail da sua conta e enviaremos um link para você criar uma nova senha.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="relative pt-1">
              <Label htmlFor="forgot-email" className="absolute left-3 -top-2 z-10 bg-background px-1 text-xs">
                E-mail
              </Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="seu@email.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                autoFocus
              />
            </div>

            <DialogFooter>
              <Button3D type="submit" className="w-full" disabled={forgotLoading}>
                {forgotLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar link de redefinição'
                )}
              </Button3D>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
